import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Camera, ArrowLeft, Loader2, X, CheckCircle2, Mic, Square, Play, Trash2 } from "lucide-react";
import QRScannerButton from "@/components/QRScannerButton";

const URGENCY_META = {
  low:      { label: "Low",      hint: "Non-blocking issue — can wait",           color: "bg-slate-100 text-slate-700 border-slate-300" },
  medium:   { label: "Medium",   hint: "Impacting output but a workaround exists", color: "bg-amber-50 text-amber-700 border-amber-300" },
  high:     { label: "High",     hint: "Line/production is at risk",                color: "bg-orange-50 text-orange-700 border-orange-300" },
  critical: { label: "Critical", hint: "Machine stopped — production halted",       color: "bg-red-50 text-red-700 border-red-300" },
};

export default function BreakdownReport() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canReport = hasCapability(user, "report_breakdown");

  const assetParam = params.get("asset") || "";
  const [assets, setAssets] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);
  const [assetKey, setAssetKey] = useState(assetParam);
  const [resolvedAsset, setResolvedAsset] = useState(null);

  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState("medium");
  const [photos, setPhotos] = useState([]);   // data URLs
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);     // created request id

  // Feb 2026 — voice-note state
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);      // preview
  const [audioUrl, setAudioUrl] = useState(null);        // objectURL for <audio>
  const [voicePayload, setVoicePayload] = useState(null); // {voice_note_path, transcript, transliterated, language}
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const mediaRecRef = useState({ current: null })[0];    // stable ref-like
  const chunksRef = useState({ current: [] })[0];
  const timerRef = useState({ current: null })[0];

  // Fetch assets for the picker (only when no ?asset= in URL)
  useEffect(() => {
    if (assetParam) return;
    setAssetLoading(true);
    api.get("/assets", { params: { limit: 500 } })
      .then((r) => setAssets(r.data || []))
      .catch(() => toast.error("Failed to load assets"))
      .finally(() => setAssetLoading(false));
  }, [assetParam]);

  // Resolve deep-linked asset (public endpoint, works even for gate-keepers etc.)
  useEffect(() => {
    if (!assetKey) return setResolvedAsset(null);
    api.get(`/assets/${assetKey}`)
      .then((r) => setResolvedAsset(r.data))
      .catch(() => {
        // Try public read-only lookup
        api.get(`/public/assets/${assetKey}`)
          .then((r) => setResolvedAsset(r.data?.asset || null))
          .catch(() => setResolvedAsset(null));
      });
  }, [assetKey]);

  const filteredAssets = useMemo(() => assets, [assets]);

  const handlePhoto = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (!f.type.startsWith("image/")) return;
      if (f.size > 2_000_000) {
        toast.error(`${f.name} is too large (max 2 MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotos((prev) => [...prev, reader.result]);
      reader.readAsDataURL(f);
    });
    e.target.value = null;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setAudioBlob(blob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
      setRecordSecs(0);
      timerRef.current = setInterval(() => {
        setRecordSecs((s) => {
          if (s >= 60) { // hard stop at 60s
            stopRecording();
            return 60;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone access denied — please allow mic permission and try again");
    }
  };
  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    setRecording(false);
  };
  const discardRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setVoicePayload(null);
    setRecordSecs(0);
  };
  const processRecording = async () => {
    if (!audioBlob) return;
    setVoiceProcessing(true);
    try {
      const fd = new FormData();
      fd.append("file", audioBlob, `voice-note.webm`);
      const { data } = await api.post("/maintenance-requests/voice-note", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      setVoicePayload({ ...data, voice_note_duration_ms: recordSecs * 1000 });
      // Auto-fill the "what is wrong" textarea with the transliterated text
      if (data.voice_note_transliterated) {
        const prefix = description.trim() ? description.trim() + "\n" : "";
        setDescription(prefix + data.voice_note_transliterated);
      }
      toast.success(data.voice_note_transliterated ? "Voice note transcribed" : "Voice note attached");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to process voice note");
    } finally {
      setVoiceProcessing(false);
    }
  };

  const submit = async () => {
    if (!assetKey) return toast.error("Please pick an asset");
    const hasDesc = description.trim().length >= 10;
    const hasVoice = !!voicePayload?.voice_note_path;
    if (!hasDesc && !hasVoice) {
      return toast.error("Describe the issue (min 10 chars) OR record a voice note");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/maintenance-requests", {
        asset_id: assetKey,
        description: description.trim(),
        urgency,
        photos,
        voice_note_path: voicePayload?.voice_note_path || null,
        voice_note_transcript: voicePayload?.voice_note_transcript || null,
        voice_note_transliterated: voicePayload?.voice_note_transliterated || null,
        voice_note_language: voicePayload?.voice_note_language || null,
        voice_note_duration_ms: voicePayload?.voice_note_duration_ms || null,
      });
      setDone(data);
      toast.success("Breakdown reported — Maintenance team notified");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not submit report");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canReport) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-semibold mb-2">You cannot report breakdowns</h1>
        <p className="text-slate-600 text-sm">
          Your role does not have the <code>report_breakdown</code> capability. Please ask your line supervisor or maintenance manager to raise the request on your behalf.
        </p>
        <Button variant="outline" className="mt-6" onClick={() => navigate(-1)} data-testid="breakdown-report-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="breakdown-report-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-semibold mb-2">Breakdown reported</h1>
        <p className="text-slate-600 mb-4">
          Ticket ID <span className="font-mono font-semibold">{done.id?.slice(0, 8)}</span> raised for asset <b>{done.asset_code}</b>.
          Maintenance team has been notified.
        </p>
        <div className="text-xs text-slate-500 mb-6">
          Assign SLA: {new Date(done.sla_assign_by).toLocaleString()}<br/>
          Resolve SLA: {new Date(done.sla_resolve_by).toLocaleString()}
        </div>
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={() => { setDone(null); setDescription(""); setPhotos([]); setUrgency("medium"); }} data-testid="breakdown-report-another-btn">
            Report another
          </Button>
          <Button onClick={() => navigate("/maintenance/kanban")} data-testid="breakdown-report-kanban-btn">
            Go to Kanban
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <Card data-testid="breakdown-report-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <CardTitle>Report a Breakdown</CardTitle>
          </div>
          <CardDescription>
            Raise a repair ticket. Maintenance manager will assign a technician.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Asset</Label>
            {assetParam ? (
              <div className="mt-1 p-3 rounded-md border bg-slate-50" data-testid="breakdown-report-asset-locked">
                {resolvedAsset ? (
                  <>
                    <div className="font-medium">{resolvedAsset.name}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {resolvedAsset.asset_id} · {resolvedAsset.location || "—"}
                    </div>
                  </>
                ) : (
                  <div className="text-slate-500 text-sm">Loading asset details…</div>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <Select value={assetKey} onValueChange={setAssetKey}>
                    <SelectTrigger className="flex-1" data-testid="breakdown-report-asset-select">
                      <SelectValue placeholder={assetLoading ? "Loading assets…" : "Pick an asset"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {filteredAssets.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          <span className="font-mono text-xs mr-2">{a.asset_id}</span>
                          {a.name} <span className="text-slate-400 ml-1">· {a.location || "—"}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <QRScannerButton
                    label="Scan"
                    size="default"
                    onDetected={(text) => {
                      // Feb 2026 — Handle every QR shape we've ever printed:
                      //  1. Raw asset UUID or asset_id ("MAC-DOME-00041")
                      //  2. JSON payload with .asset_id (older label format)
                      //  3. URL with ?asset=<id> query param
                      //  4. URL with /public/asset/<UUID> in the path (CURRENT format)
                      let value = text;
                      try {
                        const u = new URL(text);
                        const q = u.searchParams.get("asset");
                        if (q) {
                          value = q;
                        } else {
                          // Look for /public/asset/<uuid> or /asset/<id> anywhere in the path
                          const m = u.pathname.match(/\/(?:public\/)?asset[s]?\/([^/?#]+)/i);
                          if (m && m[1]) value = decodeURIComponent(m[1]);
                        }
                      } catch { /* not a URL — accept raw string */ }
                      setAssetKey(value);
                      toast.success(`Scanned asset ${value}`);
                    }}
                  />
                </div>
                {resolvedAsset && (
                  <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    <b>{resolvedAsset.asset_id}</b> · {resolvedAsset.name} · {resolvedAsset.location || "—"}
                  </p>
                )}
                {!resolvedAsset && assetKey && (
                  <p className="text-xs text-red-600 mt-1">Asset not found — try again or pick from the dropdown.</p>
                )}
              </>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>What is wrong?</Label>
              {!recording && !audioBlob && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={startRecording}
                  data-testid="breakdown-record-btn"
                  className="text-xs h-8 border-[#7C1D3A] text-[#7C1D3A] hover:bg-[#fcf0f3]"
                >
                  <Mic className="w-3.5 h-3.5 mr-1.5" /> Record voice note
                </Button>
              )}
              {recording && (
                <Button
                  type="button"
                  size="sm"
                  onClick={stopRecording}
                  data-testid="breakdown-stop-recording-btn"
                  className="text-xs h-8 bg-red-600 hover:bg-red-700 animate-pulse"
                >
                  <Square className="w-3.5 h-3.5 mr-1.5 fill-white" /> Stop · {recordSecs}s
                </Button>
              )}
            </div>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Motor is overheating and stopping after 5 minutes of running. Smells burnt.  OR tap 🎙 above to record a voice note in any language."
              className="mt-1"
              data-testid="breakdown-report-desc"
            />

            {audioBlob && (
              <div className="mt-2 flex items-center gap-2 p-2 border border-slate-200 bg-slate-50 rounded-md" data-testid="breakdown-voice-preview">
                <audio src={audioUrl} controls className="h-8 flex-1" />
                {!voicePayload && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={processRecording}
                    disabled={voiceProcessing}
                    data-testid="breakdown-process-voice-btn"
                    className="bg-[#7C1D3A] hover:bg-[#6c1c36] h-8"
                  >
                    {voiceProcessing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                    Transcribe
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={discardRecording}
                  disabled={voiceProcessing}
                  data-testid="breakdown-discard-voice-btn"
                  className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
            {voicePayload && (
              <div className="mt-1 text-xs text-emerald-700 flex items-start gap-1">
                <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  Voice note attached
                  {voicePayload.voice_note_language && voicePayload.voice_note_language !== "en" &&
                    ` (${voicePayload.voice_note_language.toUpperCase()} → Roman script)`}
                  . The transcribed text has been added above — edit if needed before submitting.
                </span>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1">
              {description.length}/500 · Describe the symptom, not the fix
            </p>
          </div>

          <div>
            <Label>Urgency</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {Object.entries(URGENCY_META).map(([key, meta]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setUrgency(key)}
                  className={`p-3 rounded-md border text-left transition ${
                    urgency === key
                      ? `${meta.color} ring-2 ring-offset-1 ring-slate-400`
                      : "bg-white border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`breakdown-report-urgency-${key}`}
                >
                  <div className="text-sm font-semibold">{meta.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{meta.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Camera className="w-4 h-4" /> Photo (optional)
            </Label>
            <Input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handlePhoto}
              className="mt-1"
              data-testid="breakdown-report-photo-input"
            />
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt={`Attachment ${i + 1}`} className="rounded-md border w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                      className="absolute -top-2 -right-2 bg-white rounded-full border p-0.5 shadow"
                      aria-label="Remove photo"
                      data-testid={`breakdown-report-photo-remove-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="breakdown-report-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submit} disabled={submitting || !assetKey} data-testid="breakdown-report-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Report Breakdown"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
